import React, { useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import { useSelector, useDispatch } from 'react-redux';
import { getOrderIncrementId, getToteSummary } from './utils/scanningUtils';
import useToteStatus from './useToteStatus';
import { setPrompt } from '../../actions/ScanToCartScanningActions';

export const PickCompleteComponent = () => {
  const {carts, activeCart, toteAssignments, cartOrders} = useSelector(state => state.scanToCart);
  const { nextPickOrderId } = useToteStatus();
  const dispatch = useDispatch();
  const currentCartId = activeCart?.cart_id;
  const isPicked = activeCart?.isPicked;

  const getEmptyCarts = (carts, currentCartId) => {
    return carts.filter(cart =>
      cart.cart_id !== currentCartId &&
      (!cart.cart_order_ids || cart.cart_order_ids.length === 0)
    );
  };

  const getProcessingOrderInfo = () => {
    if (!isPicked || !toteAssignments || !cartOrders) return null;

    if (!nextPickOrderId) return null;

    const orderIncrementId = getOrderIncrementId(cartOrders, nextPickOrderId);
    const toteSummary = getToteSummary(toteAssignments, nextPickOrderId);

    return {
      orderIncrementId,
      toteSummary
    };
  };

  const processingOrderInfo = getProcessingOrderInfo();
  const emptyCarts = getEmptyCarts(carts, currentCartId);
  const showAllCartsInProgress = emptyCarts.length === 0;
  const availableCarts = emptyCarts.map(cart => cart.cart_id);

  useEffect(() => {
    if (isPicked && processingOrderInfo && nextPickOrderId && processingOrderInfo.toteSummary) {
      const { toteSummary } = processingOrderInfo;

      if (toteSummary.length > 1) {
        const unprintedTotes = toteSummary?.filter(tote => !tote.isPrinted);

        if (unprintedTotes?.length > 0) {
          dispatch(setPrompt(true, toteSummary, 'multiToteVerification'));
        }
      }
    }
  }, [isPicked, nextPickOrderId, dispatch]);

  if (isPicked && processingOrderInfo) {
    const { orderIncrementId, toteSummary } = processingOrderInfo;
    
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.processingTitle}>Processing Order</Text>
          <Text style={styles.orderIncrementId}>{orderIncrementId}</Text>
        </View>

        <View style={styles.toteInfoContainer}>
          {toteSummary.map((tote, index) => (
            <View key={index} style={styles.toteInfoRow}>
              <Text style={styles.toteInfo}>
                {tote.tote_id} with {tote.total_qty} {tote.total_qty === 1 ? 'Item' : 'Items'}
              </Text>
              {tote.isPrinted && (
                <View style={styles.checkmarkContainer}>
                  <Text style={styles.checkmark}>✓</Text>
                </View>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.congratulationsText}>{i18n.t('pickComplete.congratulations')}</Text>
        <View style={styles.pickCompleteContainer}>
          <Text style={styles.pickCompleteText}>{i18n.t('pickComplete.pickComplete')} 🏁</Text>
        </View>
      </View>

      {showAllCartsInProgress ? (
        <>
          <Text style={styles.primaryMessage}>
            {i18n.t('pickComplete.allCartsInProgress')}
          </Text>
          
          <Text style={styles.secondaryMessage}>
            {i18n.t('pickComplete.clearCartInstructions')}
          </Text>
        </>
      ) : (
        <Text style={styles.primaryMessage}>
          {i18n.t('pickComplete.newBatchInstructions')}
        </Text>
      )}

      {availableCarts.length > 0 && (
        <View style={styles.cartsContainer}>
          <Text style={styles.cartsText}>
            {availableCarts.join(', ')}
          </Text>
        </View>
      )}

      <Text style={styles.messageText}>
        {i18n.t('pickComplete.processOrdersInstructions')}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 10,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: 20,
  },
  congratulationsText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#39CE8D',
    fontFamily: 'Poppins_600SemiBold',
    marginBottom: 8,
    textAlign: 'center',
  },
  pickCompleteContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pickCompleteText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#39CE8D',
    fontFamily: 'Poppins_600SemiBold',
  },
  processingTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#39CE8D',
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
  },
  orderIncrementId: {
    fontSize: 20,
    color: '#39CE8D',
    fontFamily: 'Poppins_400Regular',
    textAlign: 'center',
    marginTop: 5,
  },
  toteInfoContainer: {
    alignItems: 'center',
    gap: 8,
  },
  toteInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  toteInfo: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#39CE8D',
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
  },
  checkmarkContainer: {
    backgroundColor: '#39CE8D',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Existing styles
  messageText: {
    color: '#39CE8D',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    fontFamily: 'Poppins_400Regular',
  },
  primaryMessage: {
    color: '#39CE8D',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 10,
    fontFamily: 'Poppins_400Regular',
  },
  secondaryMessage: {
    color: '#39CE8D',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 10,
    fontFamily: 'Poppins_400Regular',
  },
  cartsContainer: {
    marginBottom: 10,
  },
  cartsText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#39CE8D',
    textAlign: 'center',
    fontFamily: 'Poppins_600SemiBold',
  },
});
